import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { theme } from 'antd';
import AddCircleIcon from '@/comp/common/icons/AddCircleIcon';
import StationOverviewIcon from '@/comp/common/icons/StationOverviewIcon';
import SalesmateBadgeIcon from '@/comp/common/icons/SalesmateBadgeIcon';
import PromptCard from './PromptCard';
import * as S from './AssistantSuggestions.styles';

const { useToken } = theme;

interface PromptItem {
  id: string;
  label: string;
  subtext: string;
  getIcon: (color: string) => React.ReactNode;
  badge?: string;
  onClick: () => void;
}

interface AssistantSuggestionsProps {
  onNewConversation: () => void;
  onPromptClick: (prompt: string) => void;
}

const AssistantSuggestions: React.FC<AssistantSuggestionsProps> = ({ onNewConversation, onPromptClick }) => {
  const { t } = useTranslation();
  const { token } = useToken();

  const prompts: PromptItem[] = useMemo(
    () => [
      {
        id: 'new-conversation',
        label: t('NewConversation'),
        subtext: t('AskingAIForHelp'),
        getIcon: () => <AddCircleIcon width={32} height={32} />,
        onClick: onNewConversation,
      },
      {
        id: '1',
        label: t('GetStationOverview'),
        subtext: t('ExploreStationOverview'),
        getIcon: () => <StationOverviewIcon width={44} height={32} />,
        onClick: () => onPromptClick(t('StationOverviewPrompt')),
      },
      {
        id: '2',
        label: t('PrepareForQuarterlyMeeting'),
        subtext: t('PrepareForQuarterlyMeeting'),
        getIcon: () => <SalesmateBadgeIcon width={75} height={32} />,
        onClick: () => onPromptClick(t('QuarterlyMeetingPrompt')),
      },
    ],
    [t],
  );

  return (
    <S.SuggestionsContainer>
      <S.HeroContent>
        <S.HeroTitle>{t('ExploreYourStationInsightsOrAskAIForHelp')}</S.HeroTitle>
        <S.HeroSubtitle>{t('YourSalesAssistantDescription')}</S.HeroSubtitle>
      </S.HeroContent>

      <S.PromptsGrid>
        {prompts.map(prompt => (
          <PromptCard
            key={prompt.id}
            icon={prompt.getIcon(token.colorPrimary)}
            title={prompt.label}
            text={prompt.subtext}
            onClick={() => prompt.onClick()}
          />
        ))}
      </S.PromptsGrid>
    </S.SuggestionsContainer>
  );
};

AssistantSuggestions.displayName = 'AssistantSuggestions';

export default AssistantSuggestions;
